import { Card } from "@/components/ui/card";

import type { UserUsage } from "../api";

/**
 * The busiest people in the window.
 *
 * Three by default because that's the useful glance — with ~12 users a longer
 * list is just the whole company re-sorted. The features-used count sits
 * alongside opens on purpose: someone with 40 opens across one feature and
 * someone with 40 across eight are doing very different things, and only the
 * second is really exercising the product.
 */
export function TopUsers({ users, limit = 3 }: { users: UserUsage[]; limit?: number }) {
  const top = users.slice(0, limit);
  if (top.length === 0) return null;

  const peak = Math.max(1, ...top.map((u) => u.events));

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Top {top.length === 1 ? "user" : `${top.length} users`}
        </h2>
        {users.length > top.length ? (
          <span className="text-xs text-muted-foreground">
            of {users.length} active
          </span>
        ) : null}
      </div>
      <ol className="flex flex-col gap-3">
        {top.map((u, i) => (
          <li key={u.user_id} className="flex items-center gap-3">
            <span className="w-4 shrink-0 text-sm font-semibold tabular-nums text-muted-foreground">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">
                {u.name ?? u.email ?? "Unknown"}
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-power"
                  style={{ width: `${Math.round((u.events / peak) * 100)}%` }}
                />
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-sm font-semibold tabular-nums">{u.events}</div>
              <div className="text-[11px] text-muted-foreground">
                {u.features_used} feature{u.features_used === 1 ? "" : "s"}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}
